// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Modal from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import InfoFill from "@store/Library/Components/Toolkit/Icons/Info/InfoFill";
import Minus from "@store/Library/Components/Toolkit/Icons/Minus";
import Plus from "@store/Library/Components/Toolkit/Icons/Plus";
import useAccordionState from "@store/Framework/Common/Hooks/useAccordionState";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { focusStyles, spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, HTMLAttributes, PropsWithChildren, useState } from "react";
import { css } from "styled-components";
import { ModalPresentationProps } from "@store/MobiusOverrides/Modal/Modal";
import translate from "@insite/client-framework/Translate";

export interface FilterDrawerSectionPresentationProps extends ExpandStyles<FilterDrawerSectionStyles> {}

export interface FilterDrawerSectionProps extends FilterDrawerSectionPresentationProps, HTMLAttributes<HTMLDivElement> {
    isAccordion?: boolean;
    isAccordionExpanded?: boolean;
    labelFor?: string;
    title?: string;
    modalText?: React.ReactNode;
    modalTitle?: string;
}

export interface FilterDrawerSectionStyles {
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    accordionTitle?: ClickablePresentationProps;
    accordionTitleIcon?: IconPresentationProps;
    contentWrapper?: InjectableCss;
    modalLink?: ClickablePresentationProps;
    modalLinkIcon?: IconPresentationProps;
    modal?: ModalPresentationProps;
}

const titleStyles = () => {
    return css`
        display: flex;
        font-size: ${x => x.theme.design.typography.size.base};
        font-weight: ${x => x.theme.design.typography.weight.bold};
        line-height: ${x => x.theme.design.typography.lineHeight.semi};
    `;
};

export const filterDrawerSectionStyles: FilterDrawerSectionStyles = {
    wrapper: {
        css: css`
            padding: ${pxToRem(12)} 0;
            border-top: 1px solid ${getColor("common.borderLight")};

            &:first-child {
                padding-top: 0;
                border-top: none;
            }

            &:last-child {
                padding-bottom: 0;
            }
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            gap: ${pxToRem(4)};
        `,
    },
    title: {
        css: titleStyles(),
    },
    accordionTitle: {
        css: css`
            ${titleStyles()}
            ${focusStyles()}
            width: 100%;
            align-items: center;
            gap: ${pxToRem(4)};
            transition: ${transitionHoverStyles()};

            &:hover {
                color: ${getColor("primary")};
            }
        `,
    },
    accordionTitleIcon: {
        size: 18,
        color: "inherit",
        css: css`
            margin-left: auto;
        `,
    },
    contentWrapper: {
        css: css`
            ${spacingStyles(pxToRem(12), null)}
        `,
    },
    modalLink: {
        css: css`
            flex-shrink: 0;
            display: flex;
            align-items: center;
        `,
    },
    modalLinkIcon: {
        src: InfoFill,
        size: 16,
    },
};

const FilterDrawerSection: FC<PropsWithChildren<FilterDrawerSectionProps>> = ({
    children,
    extendedStyles,
    isAccordion,
    isAccordionExpanded: baseIsAccordionExpanded = true,
    labelFor,
    overridedStyles,
    title,
    modalText,
    modalTitle,
    ...htmlAttributes
}) => {
    const styles = useExpandProps(filterDrawerSectionStyles, extendedStyles, overridedStyles);
    const { isExpanded: isAccordionExpanded, triggerId, panelId, toggle } = useAccordionState(baseIsAccordionExpanded);
    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
        <StyledWrapper {...styles.wrapper} {...htmlAttributes}>
            {(title || modalText) && (
                <StyledWrapper {...styles.titleWrapper}>
                    {title ? (
                        isAccordion ? (
                            <Clickable
                                {...styles.accordionTitle}
                                id={triggerId}
                                aria-controls={panelId}
                                aria-expanded={isAccordionExpanded}
                                onClick={toggle}
                            >
                                {title}
                                {modalText && (
                                    <Clickable
                                        {...styles.modalLink}
                                        data-test-selector="filterDrawerSection_modalLink"
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            setIsModalOpen(true);
                                        }}
                                        aria-label={`${title} ${translate("Tooltip")}`}
                                    >
                                        <IconMemo {...styles.modalLinkIcon} title={title} />
                                    </Clickable>
                                )}
                                <Icon {...styles.accordionTitleIcon} src={isAccordionExpanded ? Minus : Plus} />
                            </Clickable>
                        ) : (
                            <Typography {...styles.title} as={labelFor ? "label" : "span"} htmlFor={labelFor}>
                                {title}
                            </Typography>
                        )
                    ) : null}
                    {modalText && !isAccordion && (
                        <Clickable
                            {...styles.modalLink}
                            data-test-selector="filterDrawerSection_modalLink"
                            onClick={() => setIsModalOpen(true)}
                            aria-label={title ? `${title} ${translate("Tooltip")}` : translate("Open Tooltip")}
                        >
                            <IconMemo {...styles.modalLinkIcon} title={title} />
                        </Clickable>
                    )}
                </StyledWrapper>
            )}
            <StyledWrapper
                {...styles.contentWrapper}
                id={isAccordion && title ? panelId : undefined}
                aria-labelledby={isAccordion && title ? triggerId : undefined}
                style={isAccordion && title && !isAccordionExpanded ? { display: "none" } : undefined}
            >
                {children}
            </StyledWrapper>
            {modalText && (
                <Modal
                    headline={modalTitle || title}
                    isOpen={isModalOpen}
                    handleClose={() => setIsModalOpen(false)}
                    {...styles.modal}
                >
                    {modalText}
                </Modal>
            )}
        </StyledWrapper>
    );
};

export default FilterDrawerSection;
